import { useCallback, useEffect, useState } from "react";
import { RootState } from "@/app/rootReducer";
import { fetchChapterActivitiesAction } from "@/features/myChapterActivity/slices/chapterActivitySlice";
import { useDispatch, useSelector } from "react-redux";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

/**
 * Filter keys for chapter activities
 */
type ChapterActivityFilters = {
  searchTerm: string;
  filter: "my" | "chapter";
};

export const useChapterActivities = () => {
  const dispatch = useDispatch();

  const activities = useSelector(
    (state: RootState) => state.chapterActivity.activities,
  );
  const totalCount = useSelector(
    (state: RootState) => state.chapterActivity.totalCount,
  );
  const loading = useSelector(
    (state: RootState) => state.chapterActivity.fetchLoading,
  );
  const error = useSelector(
    (state: RootState) => state.chapterActivity.fetchError,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "chapterActivityDate", desc: true },
  ]);

  const [filters, setFilters] = useState<ChapterActivityFilters>({
    searchTerm: "",
    filter: "my",
  });

  const refetchActivities = useCallback(() => {
    const sortBy = sorting.length ? sorting[0].id : "chapterActivityDate";
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "DESC"
        : "ASC"
      : "DESC";

    dispatch(
      fetchChapterActivitiesAction({
        searchTerm: filters.searchTerm || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortOrder,
        filter: filters.filter,
      }),
    );
  }, [sorting, filters, pagination, dispatch]);

  useEffect(() => {
    refetchActivities();
  }, [refetchActivities]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value as any,
    }));
    // Reset page to 0 whenever filters change
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  };

  return {
    activities,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    refetchActivities,
  };
};
